import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape TEN-03 publishes, imported rather than restated.
import type { Entitlement } from "@tdelivr/api-client/generated/tenant/tenant-entitlement.js";

import { boundedNote, PER_FETCH, type BoardRow } from "../board.ts";
import { entitlementRow, filterEntitlements, ENTITLEMENT_STATES } from "../entitlements.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * Plan and entitlements: what this tenant has bought from the platform.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `plan`, against TEN-03
 * `GET /v1/tenants/{tenantId}/entitlements`.
 *
 * **The absence this listing closes is the one with no workaround.** An entitlement was readable
 * one `capability` at a time, capabilities are free-form strings, and nothing published a
 * vocabulary of them — so there was no first request this board could make. `Brand and domains`
 * records the same dead end from its own side, and its `Plan · Growth` tile is still unsourced for
 * the reason below.
 *
 * **`Used` is half of every row the design draws and nothing meters it.** `312 of 500`,
 * `4,610,220 of 5,000,000`, `caution · 92% used`. The limit is published; the consumption is not,
 * by any operation on this platform, and TEN-03 deliberately does not count — its responsibility is
 * what a tenant may do, not what it did. Q-105, whose recorded decision is that **no percentage
 * badge is drawn**: a percentage of an unknown numerator is worse than a blank.
 *
 * **Nor is the plan itself published.** `Plan · Growth`, `annual` and `Renews · 1 Jan 2027` have no
 * field anywhere, because the platform models entitlements and not the plan they were sold as. So
 * three of the design's four tiles are the `Used` absence and the fourth is this one.
 *
 * **No write requirement, and the design's own primary action is why.** Its form is
 * `Change plan → Request change`, which asks an account manager for a different plan; the only
 * write here is `PUT …/entitlements/{capability}`, a platform-operator grant. Asking a tenant
 * administrator's role for `tenant:entitlement:write` would be asking for the permission to raise
 * their own limits — the same reasoning the audit board applies to `audit:write`.
 */

const entitlementsApi = services.tenant__tenant_entitlement;

export interface PlanEntitlementsProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
  /**
   * The tenant in the path, which this operation takes and the zones board's did not.
   *
   * Not read from the transport: that sends the tenant as a header and has no way to hand it back.
   * The service checks the path against the verified token tenant and refuses a mismatch, so this
   * being a prop is a convenience rather than a trust decision.
   */
  readonly tenantId: string;
  /** Injected so the effective-window rule can be exercised without waiting for a date to pass. */
  readonly now?: () => number;
}

interface Loaded {
  readonly entitlements: readonly Entitlement[];
  readonly truncated: boolean;
}

export function PlanEntitlements({
  transport,
  crumbs,
  tenantId,
  now,
}: PlanEntitlementsProps): ReactNode {
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    const client = entitlementsApi.createTenantEntitlementClient(transport);
    try {
      const answered = await client.listTenantEntitlements(tenantId, PER_FETCH, undefined);
      setLoaded({
        entitlements: answered.entitlements,
        truncated: typeof answered.nextAfter === "string" && answered.nextAfter.length > 0,
      });
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["tenant:entitlement:read"]));
      setLoaded(undefined);
    }
  }, [transport, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  const instant = now === undefined ? Date.now() : now();

  const rows: readonly BoardRow[] | undefined = useMemo(() => {
    if (loaded === undefined) return undefined;
    return filterEntitlements(loaded.entitlements, state, instant).map((entitlement) =>
      entitlementRow(entitlement, instant),
    );
  }, [loaded, state, instant]);

  const bounded = boundedNote(
    loaded?.entitlements.length,
    loaded?.truncated ?? false,
    "entitlements",
    "ordered by capability",
  );

  return (
    <RecordsBoard
      crumbs={crumbs ?? []}
      title="Plan and entitlements"
      source="TEN-03"
      /*
       * The design's second clause is removed. "What your business has bought from the platform,
       * and how much of it you use" — the second half is the `Used` column, and promising it in the
       * sentence under the title while the column reads `—` is a worse page than not promising it.
       * Q-105.
       */
      description="What your business has bought from the platform. Nothing on the platform meters how much of it you use."
      columns={["Entitlement", "Limit", "Used"]}
      rows={rows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="Entitlements appear here as soon as the platform grants this tenant a capability."
      primary={{
        label: "Change plan",
        /*
         * Drawn, and refused. The design's form is three fields — Plan, Billing, and a note for an
         * account manager — and its button reads `Request change`. Nothing on this platform models
         * a plan, a billing period or an account manager, and the only write on TEN-03 is a
         * platform-operator grant of one capability. A tenant administrator pressing this would
         * either be raising their own limits or sending a message nowhere.
         */
        refusal:
          "Nothing on this platform models a plan, a billing period or an account manager. TEN-03's only write grants one capability and is a platform-operator action, so a tenant administrator's role does not hold it. Q-105.",
      }}
      filter={{
        unfiltered: "All states",
        options: ENTITLEMENT_STATES.map((value) => ({ value, label: value })),
        value: state,
        onChange: setState,
      }}
      note={{
        /*
         * The design's `info` note is replaced and keeps its kind, its glyph and its place.
         *
         * It reads "Upgrades take effect immediately; downgrades at renewal." Both halves are
         * statements about a plan, and no plan exists: what TEN-03 holds is one grant per capability
         * with its own effective window, and a window is not a renewal date. Q-105.
         */
        kind: "info",
        glyph: "info",
        text: "Each grant has its own effective window, and a service asks for the one in force at the moment it checks. A grant outside its window is not usable, whatever its row says it is enabled for.",
      }}
      {...(bounded === undefined ? {} : { bounded })}
      footnote="Every entitlement this tenant holds, in force or not: the listing is deliberately unfiltered by effectiveness, because a capability that starts next month is part of what the business has bought."
      exportName="entitlements"
      detailSubtitle="Plan and entitlements · TEN-03"
      detailNote="Each grant has its own effective window, and a service asks for the one in force at the moment it checks. A grant outside its window is not usable, whatever its row says it is enabled for."
    />
  );
}
